import { t } from "@lingui/macro";
import {
  NavigatorScreenParams,
  RouteProp,
  useRoute,
} from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { Suspense, useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { BookingType } from "@/screens/BookingDetail/NewPatronBookingFlow/components/BookingSummary/components/MoveBookingButton/__generated__/MoveBookingButton_Booking.graphql";
import { DepartmentAvailabilityScreen } from "@/screens/DepartmentAvailability";
import { StoreScreen } from "@/screens/Store";
import { StoreStackNavigatorParamList } from "@/screens/Store/components/StoreStackNavigator";
import { Loader } from "@whoosh/design-system/src/components/display";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";

import { PageWrapper, useShowPageHeader } from "../../components/PageWrapper";
import { Topbar } from "../../components/Topbar";
import { BiometricOptInModal } from "../../features/BiometricAuthentication";
import { LocaleTransitionBoundary } from "../../features/LanguagePreference";
import { LessonFacilitySwitcherModal } from "../../features/Lessons/LessonFacilitySwitcher";
import { showClubSwitcherModalAtom } from "../../features/Session";
import { ClubSwitcher } from "../../features/Session";
import { useCurrentClubHasLessonsEnabled } from "../../features/Session/hooks/useCurrentClubHasLessonsEnabled";
import { AgendaScreen } from "../../screens/AgendaScreen";
import { BookingSummary } from "../../screens/BookingDetail/NewPatronBookingFlow/components/BookingSummary/BookingSummary";
import { ClubLoginScreen } from "../../screens/ClubLogin";
import { EventDetailStackNavigator } from "../../screens/EventDetail";
import { EventSignupSummaryScreen } from "../../screens/EventSignupSummary/EventSignupSummary";
import { JoinBookingScreen } from "../../screens/JoinBooking/JoinBookingScreen";
import { MoveBookingScreen } from "../../screens/MoveBooking/MoveBookingScreen";
import { MoveDiningBookingScreen } from "../../screens/MoveDiningBooking/MoveDiningBookingScreen";
import { useFlags } from "../../utils/launchDarkly";
import { BaseNavigatorParamList } from "../BaseNavigator/types";
import { HomeTabsNavigator } from "../HomeTabsNavigator";
import { HomeTabsNavigatorParamList } from "../HomeTabsNavigator/types";
import { LessonsStackNavigator } from "../LessonsStackNavigator";
import { MasqueradeBar } from "./MasqueradeBarPatron";
import { ClubStackNavigatorFragment_session$key } from "./__generated__/ClubStackNavigatorFragment_session.graphql";
import { OnboardingModal } from "./components/OnboardingModal";

export type ClubStackNavigatorParamList = {
  Home:
    | NavigatorScreenParams<HomeTabsNavigatorParamList> & {
        clubSlug?: string;
      };
  Onboarding: undefined;
  BookingSummary: {
    slotOrBookingId: string;
    fromUrl?: boolean;
    params?: {
      slotIds?: string[];
      bookingIds?: string[];
      currentRequestBookingId?: string | null;
      pendingRecurrence?: {
        recurrenceType: "DAY" | "WEEK" | "MONTH";
        repeatInterval: number;
        endCondition: "ON_DATE" | "AFTER_OCCURRENCES";
        endDate?: string;
        occurrenceCount?: number;
      };
    };
  };
  JoinBooking: { joinableId: string };
  MoveBooking: {
    date: string;
    originalSlotId: string;
    originalBookingId: string;
    originalFacilityId: string;
    facilitySlug: string;
    bookingType: BookingType;
  };
  MoveDiningBooking: {
    bookingId: string;
    date?: string;
  };
  EventDetail: {
    eventID: string;
    joinEvent?: boolean;
    openSignupStep?: string;
  };
  EventSignupSummary: {
    eventID: string;
    signUpId: string;
  };
  Agenda: {
    facilitySlug: string;
    date?: string;
    startHour?: number;
    courseId?: string;
    timeLTE?: string;
    timeGTE?: string;
    groupSize?: number;
    holeCount?: HoleCount;
    emailOrPhone?: string;
  };
  DepartmentAvailability: {
    departmentSlug: string;
    date?: string;
  };
  Lessons: { facilitySlug?: string; screen: any };
  InstructorDetail: {
    facilitySlug: string;
    instructorId: string;
    date?: string;
  };
  MyReservations: undefined;
  MyBookings: undefined;
  Login: { clubSlug?: string; routeAfterLogin?: string };
  Store: NavigatorScreenParams<StoreStackNavigatorParamList> & {
    clubSlug?: string;
  };
};

const Stack = createNativeStackNavigator<ClubStackNavigatorParamList>();

type Props = {
  sessionRef: ClubStackNavigatorFragment_session$key;
  initialRouteName?: keyof ClubStackNavigatorParamList;
  // When a logged-out visitor lands on a private club, render only the Login route
  // so React Navigation owns the /login URL natively (no inline render or manual
  // history rewriting).
  requiresLogin?: boolean;
};

export const ClubStackNavigatorStack = ({
  sessionRef,
  initialRouteName = "Home",
  requiresLogin = false,
}: Props) => {
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  const session = useFragment(ClubStackNavigatorFragmentString, sessionRef);
  const lessonsEnabled = useCurrentClubHasLessonsEnabled(session);

  const isMember =
    session?.currentClientProfile?.member?.clientClass === "MEMBER";
  const memberProfile = session.currentClientProfile;

  const [showClubSwitcherModal, setShowClubSwitcherModal] = useAtom(
    showClubSwitcherModalAtom
  );

  /**
   * If we are on a profile that hasn't onboarded yet - show the modal
   */
  useEffect(() => {
    if (
      isMember &&
      memberProfile &&
      memberProfile.id &&
      !memberProfile?.onboardedAt
    ) {
      setShowOnboardingModal(true);
    }
  }, [isMember, memberProfile]);

  const { newTopbar } = useFlags();
  const route = useRoute<RouteProp<BaseNavigatorParamList, "Club">>();
  const homeScreen =
    route.params?.screen === "Home" ? route.params.params?.screen : undefined;
  const { showFixedTopbar } = useShowPageHeader({
    isNavigatingToNestedHomeScreen:
      homeScreen !== undefined && homeScreen !== "MainHome",
  });

  return (
    <>
      <PageWrapper header={newTopbar && showFixedTopbar ? <Topbar /> : null}>
        <Stack.Navigator
          key={requiresLogin ? "login" : "authenticated"}
          initialRouteName={requiresLogin ? "Login" : initialRouteName}
          screenLayout={({ children, route: screenRoute }) =>
            screenRoute.name === "Home" ? (
              children
            ) : (
              <LocaleTransitionBoundary>{children}</LocaleTransitionBoundary>
            )
          }
        >
          {requiresLogin ? (
            <Stack.Screen
              name="Login"
              component={ClubLoginScreen}
              options={{ headerShown: false, title: t`Club Login` }}
              initialParams={{ clubSlug: route.params.clubSlug }}
            />
          ) : (
            <>
              <Stack.Screen
                name={"Home"}
                options={{ headerShown: false, title: t`Bookings` }}
              >
                {() => <HomeTabsNavigator sessionRef={session} />}
              </Stack.Screen>
              <Stack.Screen
                name="BookingSummary"
                component={BookingSummary}
                options={{ headerShown: false, title: t`Bookings` }}
              />
              <Stack.Screen
                name="JoinBooking"
                component={JoinBookingScreen}
                options={{ headerShown: false, title: t`Join Booking` }}
              />
              <Stack.Screen
                name="MoveBooking"
                component={MoveBookingScreen}
                options={{ headerShown: false, title: t`Move Booking` }}
              />
              <Stack.Screen
                name="MoveDiningBooking"
                component={MoveDiningBookingScreen}
                options={{ headerShown: false, title: t`Move Dining Booking` }}
              />
              <Stack.Screen
                name="EventDetail"
                component={EventDetailStackNavigator}
                options={{ headerShown: false, title: t`Events` }}
              />
              <Stack.Screen
                name="EventSignupSummary"
                component={EventSignupSummaryScreen}
                options={{ headerShown: false, title: t`Event Signup Summary` }}
              />
              <Stack.Screen
                name="Agenda"
                component={AgendaScreen}
                options={{ headerShown: false, title: t`Agenda` }}
              />
              <Stack.Screen
                name="DepartmentAvailability"
                component={DepartmentAvailabilityScreen}
                options={{
                  headerShown: false,
                  title: t`Department Availability`,
                }}
              />
              {lessonsEnabled ? (
                <Stack.Screen
                  name="Lessons"
                  component={LessonsStackNavigator}
                  options={{ headerShown: false, title: t`Lessons` }}
                />
              ) : null}
              <Stack.Screen
                name="Login"
                component={ClubLoginScreen}
                options={{ headerShown: false, title: t`Club Login` }}
                initialParams={{ clubSlug: route.params.clubSlug }}
              />
              <Stack.Screen
                name="Store"
                component={StoreScreen}
                options={{ headerShown: false, title: t`Store` }}
                initialParams={{ clubSlug: route.params.clubSlug }}
              />
            </>
          )}
        </Stack.Navigator>
      </PageWrapper>
      {memberProfile?.id ? (
        <OnboardingModal
          showModal={showOnboardingModal}
          setShowModal={(val) => {
            setShowOnboardingModal(val);
          }}
        />
      ) : null}
      {memberProfile?.onboardedAt ? (
        <BiometricOptInModal
          email={session.user?.email}
          userId={session.user?.id}
        />
      ) : null}
      {session.user?.__typename === "MemberUser" ? (
        <ClubSwitcher
          isOpen={showClubSwitcherModal}
          onClose={() => setShowClubSwitcherModal(false)}
          userRef={session.user}
          sessionRef={session}
          currentClubSlug={
            session.currentClientProfile?.member?.club?.slug ?? ""
          }
        />
      ) : null}
      <LessonFacilitySwitcherModal sessionRef={session} />
      {Platform.OS === "web" && (
        <Suspense fallback={<Loader />}>
          {session ? <MasqueradeBar sessionRef={session} /> : null}
        </Suspense>
      )}
    </>
  );
};

export const ClubStackNavigatorFragmentString = graphql`
  fragment ClubStackNavigatorFragment_session on Session {
    ...ClubSwitcherFragment_session
    ...HomeTabsNavigator_session
    ...LessonFacilitySwitcherModalFragment_session
    ...MasqueradeBarPatronFragment_session
    ...useCurrentClubHasLessonsEnabled_session
    user {
      id
      email
      __typename
      ... on MemberUser {
        ...ClubSwitcherFragment_memberUser
      }
    }
    currentClientProfile {
      member {
        clientClass
        club {
          slug
        }
      }
      ... on MemberProfile {
        id
        onboardedAt
      }
    }
  }
`;
